import { FunctionComponent, JSX, useCallback, useMemo } from 'react'
import { DaycareType } from '@/types/Daycare'
import { ChildDaycare } from '@/types/Child'
import styles from './sectionSelector.module.scss'
import { useTranslation } from 'react-i18next'
import { ChildChecked, ParentChecked, SectionAndChild, SectionAndParent, SectionSelectorProps } from './types'
import { useSectionChildSelection, UseSectionChildSelectionReturn } from './useSectionChildSelection'
import { useParentSelection, UseParentSelectionReturn } from './useParentSelection'
import SectionMenu, { MenuItem } from './SectionMenu'

const SectionSelector: FunctionComponent<SectionSelectorProps> = ({
    daycare,
    sections,
    parents,
    postChilds,
    setPostChilds,
    checkAllByDefault = true
}: SectionSelectorProps) => {
    const { t } = useTranslation()

    const isRpeOrLaep: boolean = useMemo(
        (): boolean => daycare?.type === DaycareType.rpe || daycare?.type === DaycareType.laep,
        [daycare?.type]
    )

    const childSelection: UseSectionChildSelectionReturn = useSectionChildSelection({
        daycare,
        sections,
        parents,
        postChilds,
        setPostChilds,
        checkAllByDefault
    })

    const parentSelection: UseParentSelectionReturn = useParentSelection({
        daycare,
        sections,
        parents,
        postChilds,
        setPostChilds
    })

    const getChildComplement: (daycares: ChildDaycare[]) => string = useCallback(
        (daycares: ChildDaycare[]): string => {
            const complement: string | undefined = daycares.find(
                (cd: ChildDaycare): boolean => cd.daycareId === daycare?.id
            )?.complement
            return complement ? ` - ${complement}` : ''
        },
        [daycare?.id]
    )

    const sortByChildName: (a: { label: string }, b: { label: string }) => number = useCallback(
        (a: { label: string }, b: { label: string }): number => a.label.localeCompare(b.label),
        []
    )

    const childToMenuItem: (child: ChildChecked) => MenuItem = useCallback(
        (child: ChildChecked): MenuItem => ({
            id: child.child.id,
            label: child.child.firstname + getChildComplement(child.child.daycares),
            isChecked: child.isCheck
        }),
        [getChildComplement]
    )

    const parentToMenuItem: (parent: ParentChecked) => MenuItem = useCallback(
        (parent: ParentChecked): MenuItem => ({
            id: parent.parent.id,
            label:
                parent.parent.lastname && parent.parent.firstname
                    ? `${parent.parent.lastname} ${parent.parent.firstname}`
                    : parent.parent.email,
            isChecked: parent.isCheck
        }),
        []
    )

    const renderChildSections: () => JSX.Element = (): JSX.Element => (
        <>
            {childSelection.sectionAndChilds.map(
                (sectionAndChild: SectionAndChild, sectionIndex: number): JSX.Element => (
                    <SectionMenu
                        key={sectionAndChild.section.id}
                        sectionName={sectionAndChild.section.name.toString()}
                        items={sectionAndChild.childs.map(childToMenuItem)}
                        onSectionChange={() => childSelection.handleSectionChange(sectionIndex)}
                        onItemChange={(childId) =>
                            childSelection.handleChildChange(sectionAndChild.section.id, childId)
                        }
                        sortFn={sortByChildName}
                    />
                )
            )}
            {childSelection.childsWithoutSection.length > 0 && (
                <SectionMenu
                    sectionName={t('withoutSection')}
                    items={childSelection.childsWithoutSection.map(childToMenuItem)}
                    onSectionChange={childSelection.handleWithoutSectionChange}
                    onItemChange={childSelection.handleChildWithoutSectionChange}
                    sortFn={sortByChildName}
                />
            )}
        </>
    )

    const renderParentSections: () => JSX.Element = (): JSX.Element => (
        <>
            {parentSelection.sectionAndParents.map(
                (sectionAndParent: SectionAndParent, sectionIndex: number): JSX.Element => (
                    <SectionMenu
                        key={sectionAndParent.section.id}
                        sectionName={sectionAndParent.section.name.toString()}
                        items={sectionAndParent.parents.map(parentToMenuItem)}
                        onSectionChange={(): void => parentSelection.handleSectionChange(sectionIndex)}
                        onItemChange={(parentId: number): void =>
                            parentSelection.handleParentChange(sectionAndParent.section.id, parentId)
                        }
                        sortFn={sortByChildName}
                    />
                )
            )}
            {parentSelection.parentsWithoutSection.length > 0 && (
                <SectionMenu
                    sectionName={t('withoutSection')}
                    items={parentSelection.parentsWithoutSection.map(parentToMenuItem)}
                    onSectionChange={parentSelection.handleWithoutSectionChange}
                    onItemChange={parentSelection.handleParentWithoutSectionChange}
                    sortFn={sortByChildName}
                />
            )}
        </>
    )

    return (
        <div className={styles.SectionSelector}>
            <p className={styles.title}>
                {t(isRpeOrLaep ? 'public' : 'child')} / {t(isRpeOrLaep ? 'sector' : 'section')}
            </p>
            <div className={styles.groupSections}>{isRpeOrLaep ? renderParentSections() : renderChildSections()}</div>
        </div>
    )
}

export default SectionSelector
